import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import CheckmarxConnectForm from "./CheckmarxConnectForm";
import checkmarx_step_1 from "./images/checkmarx_1_navigation.png";
import checkmarx_step_2 from "./images/checkmarx_2_api_key_modal.png";
import checkmarx_step_3 from "./images/checkmarx_3_api_key_created.png";
import checkmarx_step_4_tenant_account from "./images/checkmarx_4_tenant_account.png";

const CheckmarxIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("checkmarx");
  const [loading, setLoading] = useState(false);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerCheckmarxIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerCheckmarxIntegration(
      values.api_key,
      values.portal_url,
      values.tenant_name
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Checkmarx integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the security scan results from Checkmarx and prioritize the suggested remediations."
            : ""
        }
        image={logos.CheckmarxLogo}
        name={"Checkmarx"}
        iconHeight={50}
        iconWidth={50}
        content={
          isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Typography variant={"h4"}>Checkmarx is integrated!</Typography>
          ) : (
            <div>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              <GuidelineStepByStep>
                <GuidelineStep title="Log in to the Checkmarx One web portal">
                  <Typography variant="body2">
                    Login with the Admin user
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Navigate to API Keys">
                  <Typography variant="body2">
                    Select{" "}
                    <strong>
                      Settings &gt; Identity and Access Management
                    </strong>{" "}
                    in the main navigation. The IAM portal opens.
                  </Typography>
                  <Typography variant="body2">
                    In the main navigation, click <strong>API Keys</strong>,
                    then click on the <strong>Create Key</strong> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: checkmarx_step_1,
                        altext: "Checkmarx API Keys page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Configure API Key">
                  <Typography variant="body2">
                    The API Key configuration window opens.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: checkmarx_step_2,
                        altext: "Checkmarx API Key configuration",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">Fill in the details:</Typography>
                  <Typography variant="body2" component="div">
                    <ul>
                      <li
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "4px",
                        }}
                      >
                        <span>
                          <b>Note:</b> Breeze Platform Key
                        </span>
                        <CustomCopyButton
                          copyTitle="Note"
                          copyText="Breeze Platform Key"
                          width="14px"
                        />
                      </li>
                      <li
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "4px",
                        }}
                      >
                        <span>
                          <b>Expiration period:</b> we recommend 365 days
                        </span>
                      </li>
                      <li style={{ fontStyle: "italic", marginLeft: "20px" }}>
                        If an administrator set the default expiration period to
                        be "enforced", then this field will be locked.
                      </li>
                      <li
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "4px",
                        }}
                      >
                        <span>
                          <b>Notification emails:</b> Enter emails of each
                          recipient who you would like to receive notifications
                          regarding expiration of the key. After entering each
                          email, click
                          <b>Add</b>. By default the email of the current user
                          is included.
                        </span>
                      </li>
                    </ul>
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Create API Key">
                  <Typography variant="body2">
                    Click <strong>Create</strong>.
                  </Typography>
                  <Typography variant="body2">
                    The API Key is created and a window opens showing the key.
                    Copy the key and paste below!
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: checkmarx_step_3,
                        altext: "Checkmarx API Key created",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Obtain Tenant Account Name">
                  <Typography variant="body2">
                    Navigate to <strong>Account Settings &gt; License</strong>{" "}
                    to find your tenant account name.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: checkmarx_step_4_tenant_account,
                        altext: "Checkmarx Account Settings License page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Connect to Breeze">
                  <Typography variant="body2">
                    Fill in the details and hit Connect.
                  </Typography>
                  <Box width={700}>
                    <CheckmarxConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                      loading={loading}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default CheckmarxIntegration;
